/**
 * graphModel — modèle de vue pur du graphe notes ↔ entrées (chantier C7).
 *
 * Transforme la réponse de `GET /api/graph` (slice `knowledgeGraph`) en ce que
 * le canevas dessine : filtres de types de nœuds et de familles d'arêtes,
 * nœuds isolés masqués (avec leur compte), degré, voisins d'un nœud, mise en
 * avant par la recherche. Aucune dépendance React : tout est testable à sec.
 */
import { noteEffective } from '../importanceWeight';
import { NOTE_RELIEE_LABELS } from '../../components/notes/notesReliees/noteRelieeLabels';
import { CORRESPONDANCE_LABELS } from '../../components/entrees/correspondances/correspondanceLabels';
import { FACE_A_FACE_LABELS } from '../../components/entrees/faceAFace/faceAFaceLabels';
import type { GraphEdge, GraphEdgeKind, GraphNode, GraphNoteNode, GraphSkosRelation, NoteType, RelationAlignement } from '../../types/api';

export type NodeKindFilter = NoteType | 'entree' | 'document';

export interface GraphFilters {
    nodeKinds: Record<NodeKindFilter, boolean>;
    edgeKinds: Record<GraphEdgeKind, boolean>;
    /** Afficher les nœuds sans aucune arête visible. */
    showIsolated: boolean;
    /** Afficher les termes liés qu'aucune note de la portée ne cite (entrées `cited: false`). */
    showUncited: boolean;
    /** Entourer les notes des groupes reliés sans concept commun (C22 SP3) ; ne masque aucun nœud. */
    showSansConcept: boolean;
    /** Texte de recherche : met en avant les nœuds correspondants (ne filtre pas). */
    search: string;
    /** Importance minimale d'une marque pour que son trait soit dessiné (1 = toutes ; une marque sans note vaut 3). */
    importanceMin: number;
    /** Calque « concepts marqués ensemble » : ne change ni les arêtes ni les degrés de la vue. */
    coMarques: boolean;
}

export const DEFAULT_GRAPH_FILTERS: GraphFilters = Object.freeze({
    nodeKinds: Object.freeze({ manual: true, source: true, commentary: true, entree: true, document: true }),
    edgeKinds: Object.freeze({ reference: true, indexation: true, skos: true, suite: true, soutient: true, oppose: true, alignement: true, citation: true, memePassage: true }),
    showIsolated: false,
    showUncited: true,
    showSansConcept: true,
    search: '',
    importanceMin: 1,
    coMarques: false,
}) as GraphFilters;

export const IMPORTANCE_PALIERS: ReadonlyArray<number> = Object.freeze([1, 2, 3, 4, 5]);

export interface GraphView {
    nodes: GraphNode[];
    edges: GraphEdge[];
    /** Degré de chaque nœud affiché (arêtes visibles). */
    degree: Map<string, number>;
    /** Nœuds retirés parce qu'isolés (filtre « nœuds isolés » désactivé). */
    hiddenIsolated: number;
    /** Nœuds correspondant à la recherche (vide si pas de recherche). */
    highlighted: Set<string>;
}

export interface GraphNeighbor {
    node: GraphNode;
    edge: GraphEdge;
}

/** Libellés humains — jamais une clé technique à l'écran. */
export const NODE_KIND_LABELS: Record<NodeKindFilter, string> = {
    manual: 'Note manuelle',
    source: 'Extrait de source',
    commentary: 'Commentaire',
    entree: 'Concept',
    document: 'Document',
};

export const EDGE_KIND_LABELS: Record<GraphEdgeKind, string> = {
    reference: 'Référence',
    indexation: 'Indexation',
    skos: 'Lien entre concepts',
    suite: 'Fait suite',
    /** 19/10/2026 : la relation d'un passage relié, les verbes des notes reliées. */
    soutient: 'Soutient',
    oppose: 'S’oppose',
    /** C23 SP4 : mon alignement, de mon concept vers celui d'une autre personne. */
    alignement: CORRESPONDANCE_LABELS.graphe,
    /** 19/10/2026 : une note prise dans un document. */
    citation: 'Citation',
    /** C23 SP6 : deux notes qui citent le même texte (carte du face à face). */
    memePassage: FACE_A_FACE_LABELS.carte.memePassage,
};

/** Ce qu'une relation dit d'un voisin, selon le sens (19/10/2026). */
export const RELATION_VOISIN_LABELS = {
    soutient: { sortante: NOTE_RELIEE_LABELS.relations.soutient, entrante: 'Soutenue par' },
    oppose: { sortante: NOTE_RELIEE_LABELS.relations.oppose, entrante: 'Contestée par' },
} as const;

/** Familles dont le libellé de pastille ne prend pas de « s ». */
export const INVARIABLE_EDGE_KINDS: ReadonlySet<GraphEdgeKind> = new Set<GraphEdgeKind>(['skos', 'suite', 'soutient', 'oppose', 'alignement', 'memePassage']);

/** Familles orientées de note à note : le trait porte une pointe vers la cible. */
export const RELATION_EDGE_KINDS: ReadonlySet<GraphEdgeKind> = new Set<GraphEdgeKind>(['suite', 'soutient', 'oppose']);

/** La relation d'un renvoi SKOS (jamais celle d'un alignement). */
export const skosRelationOf = (edge: GraphEdge): GraphSkosRelation | undefined =>
    (edge.kind === 'skos' && (edge.relation === 'BT' || edge.relation === 'RT' || edge.relation === 'USE') ? edge.relation : undefined);

/** La relation d'un alignement (C23 SP4), ou rien. */
export const alignementRelationOf = (edge: GraphEdge): RelationAlignement | undefined =>
    (edge.kind === 'alignement' && !!edge.relation && edge.relation in CORRESPONDANCE_LABELS.relationsEnPhrase ? edge.relation as RelationAlignement : undefined);

export const SKOS_RELATION_LABELS = {
    BT: 'terme générique',
    RT: 'terme associé',
    USE: 'terme préféré',
} as const;

/** Libellé humain des termes liés non cités (légende, pastille-filtre). */
export const UNCITED_LABEL = 'Termes non cités';

/** Relations SKOS orientées : dessinées avec une flèche vers la cible (générique, préféré). */
export const ORIENTED_SKOS_RELATIONS: ReadonlySet<GraphSkosRelation> = new Set<GraphSkosRelation>(['BT', 'USE']);

/** Arête tracée avec une flèche vers sa cible : renvoi SKOS orienté, ou suite (de la note qui suit vers celle qu'elle suit). */
export function isOrientedEdge(edge: GraphEdge): boolean {
    if (RELATION_EDGE_KINDS.has(edge.kind)) return true;
    const relation = skosRelationOf(edge);
    return !!relation && ORIENTED_SKOS_RELATIONS.has(relation);
}

/** Ce qu'une arête dit d'un voisin, vu depuis `nodeId` (fiche de sélection). */
export function neighborLabel(edge: GraphEdge, nodeId: string): string {
    if (edge.kind === 'suite') return edge.source === nodeId ? NOTE_RELIEE_LABELS.faitSuiteA : NOTE_RELIEE_LABELS.suiviePar;
    if (edge.kind === 'soutient' || edge.kind === 'oppose') {
        const sens = RELATION_VOISIN_LABELS[edge.kind];
        return edge.source === nodeId ? sens.sortante : sens.entrante;
    }
    const skos = skosRelationOf(edge);
    if (skos) return `${EDGE_KIND_LABELS.skos} · ${SKOS_RELATION_LABELS[skos]}`;
    const alignement = alignementRelationOf(edge);
    if (alignement) return `${EDGE_KIND_LABELS.alignement} · ${CORRESPONDANCE_LABELS.relations[alignement]}`;
    return EDGE_KIND_LABELS[edge.kind];
}

/**
 * La note d'importance posée sur une arête d'indexation (meilleur passage, C22 SP0), ou `undefined` :
 * une marque sans note ne dit rien — on n'affiche jamais un 3 que personne n'a posé.
 */
export function importanceDeLArete(edge: GraphEdge): number | undefined {
    return edge.kind === 'indexation' && typeof edge.rating === 'number' && noteEffective(edge.rating) === edge.rating ? edge.rating : undefined;
}

/** Phrase d'infobulle d'une arête : « Terme générique : Chien → Mammifère ». */
export function edgeTitle(edge: GraphEdge, source: GraphNode | undefined, target: GraphNode | undefined): string {
    const from = source ? displayLabel(source) : '';
    const to = target ? displayLabel(target) : '';
    const skos = skosRelationOf(edge);
    if (skos) {
        const relation = SKOS_RELATION_LABELS[skos];
        const label = relation.charAt(0).toUpperCase() + relation.slice(1);
        return ORIENTED_SKOS_RELATIONS.has(skos) ? `${label} : ${from} → ${to}` : `${label} : ${from} ↔ ${to}`;
    }
    // Alignement (C23 SP4) : la phrase de qui l'a posé, son concept d'abord — « Correspondance : « libre arbitre » correspond à peu près à « Liberté » ».
    const alignement = alignementRelationOf(edge);
    if (alignement) return `${EDGE_KIND_LABELS.alignement} : ${CORRESPONDANCE_LABELS.phrase(from, alignement, to)}`;
    // Même passage (C23 SP6) : sans sens — les deux notes citent le même texte.
    if (edge.kind === 'memePassage') return `${EDGE_KIND_LABELS.memePassage} : ${from} ↔ ${to}`;
    const titre = `${EDGE_KIND_LABELS[edge.kind]} : ${from} → ${to}`;
    // Note d'importance posée sur le passage (C22 SP0) : dite seulement quand elle existe.
    const importance = importanceDeLArete(edge);
    return importance !== undefined ? `${titre} · importance ${importance}` : titre;
}

export const UNTITLED_NOTE = 'Note sans titre';

export const nodeKindOf = (node: GraphNode): NodeKindFilter => (node.kind === 'note' ? node.noteType : node.kind);

export const displayLabel = (node: GraphNode): string => node.label || (node.kind === 'note' ? UNTITLED_NOTE : node.kind === 'document' ? 'Document' : 'Concept');

/** Une note d'une autre personne (lisible par partage) : dessinée hachurée (19/10/2026). */
export const isAutrui = (node: GraphNode): boolean => node.kind === 'note' && !node.isOwn;

/** Entrée liée au voisinage mais citée par aucune note de la portée. */
export const isUncited = (node: GraphNode): boolean => node.kind === 'entree' && node.cited === false;

/** Un foyer (19/10/2026) : ce que la personne a choisi de voir, ou un membre de ce qu'elle a choisi. */
export const isFoyer = (node: GraphNode): boolean => node.foyer === true;

/** Note rattachée à plusieurs périmètres : un pont entre eux. */
export const isBridge = (node: GraphNode): node is GraphNoteNode => node.kind === 'note' && node.perimetres.length > 1;

/** Minuscules sans accents : « Âme » et « ame » se retrouvent. */
export function normalizeSearch(text: string): string {
    return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

export function matchesSearch(label: string, query: string): boolean {
    const q = normalizeSearch(query);
    if (!q) return false;
    return normalizeSearch(label).includes(q);
}

/**
 * Rayon d'un nœud : 6 + 3·√degré, borné à 22 (les hubs restent lisibles sans écraser le reste).
 * Sur la carte du face à face (C23 SP6), une note porte `importance` (1 à 5) : le rayon la suit —
 * 7 + 3 × importance, de 10 à 22 px — et le degré ne compte plus. Sans importance, la règle du degré.
 */
export function nodeRadius(degree: number, importance?: number): number {
    if (typeof importance === 'number' && Number.isFinite(importance)) {
        const palier = Math.max(1, Math.min(5, Math.round(importance)));
        return 7 + 3 * palier;
    }
    return Math.min(22, 6 + 3 * Math.sqrt(Math.max(0, degree)));
}

/** Le rayon d'un nœud de la vue : l'importance d'une note quand elle est dite, sinon son degré. */
export const rayonDuNoeud = (view: GraphView, node: GraphNode): number =>
    nodeRadius(view.degree.get(node.id) ?? 0, node.kind === 'note' ? node.importance : undefined);

export function buildGraphView(
    data: { nodes: GraphNode[]; edges: GraphEdge[] } | null | undefined,
    filters: GraphFilters = DEFAULT_GRAPH_FILTERS,
): GraphView {
    const empty: GraphView = { nodes: [], edges: [], degree: new Map(), hiddenIsolated: 0, highlighted: new Set() };
    if (!data) return empty;

    const keptNodes = new Map<string, GraphNode>();
    for (const node of data.nodes) {
        if (!filters.nodeKinds[nodeKindOf(node)]) continue;
        if (!filters.showUncited && isUncited(node)) continue;
        keptNodes.set(node.id, node);
    }

    const degree = new Map<string, number>();
    const edges: GraphEdge[] = [];
    const importanceMin = Math.max(1, Math.min(5, Math.round(filters.importanceMin ?? 1)));
    for (const edge of data.edges) {
        if (!filters.edgeKinds[edge.kind]) continue;
        if (!keptNodes.has(edge.source) || !keptNodes.has(edge.target) || edge.source === edge.target) continue;
        // Importance minimale (19/10/2026) : une marque plus faible que le seuil ne trace rien.
        if (edge.kind === 'indexation' && importanceMin > 1 && noteEffective(edge.rating) < importanceMin) continue;
        edges.push(edge);
        degree.set(edge.source, (degree.get(edge.source) ?? 0) + 1);
        degree.set(edge.target, (degree.get(edge.target) ?? 0) + 1);
    }

    let hiddenIsolated = 0;
    const nodes: GraphNode[] = [];
    for (const node of keptNodes.values()) {
        const d = degree.get(node.id) ?? 0;
        // Un foyer reste visible même seul : la personne l'a choisi, le graphe ne peut pas le taire.
        if (d === 0 && !filters.showIsolated && !isFoyer(node)) { hiddenIsolated += 1; continue; }
        degree.set(node.id, d);
        nodes.push(node);
    }

    const highlighted = new Set<string>();
    if (normalizeSearch(filters.search)) {
        for (const node of nodes) {
            if (matchesSearch(displayLabel(node), filters.search)) highlighted.add(node.id);
        }
    }

    return { nodes, edges, degree, hiddenIsolated, highlighted };
}

const kindOrder = (node: GraphNode): number => (node.kind === 'entree' ? 0 : 1);

/** Voisins d'un nœud : entrées d'abord puis notes, par libellé ; l'arête dit la famille du lien. */
export function neighborsOf(view: GraphView, nodeId: string): GraphNeighbor[] {
    const byId = new Map(view.nodes.map(n => [n.id, n]));
    const out: GraphNeighbor[] = [];
    const seen = new Set<string>();
    for (const edge of view.edges) {
        const otherId = edge.source === nodeId ? edge.target : edge.target === nodeId ? edge.source : null;
        if (!otherId) continue;
        const sens = edge.kind === 'suite' ? (edge.source === nodeId ? '|sortante' : '|entrante') : '';
        const key = `${otherId}|${edge.kind}${sens}`;
        if (seen.has(key)) continue;
        const node = byId.get(otherId);
        if (!node) continue;
        seen.add(key);
        out.push({ node, edge });
    }
    out.sort((a, b) => (kindOrder(a.node) - kindOrder(b.node))
        || displayLabel(a.node).localeCompare(displayLabel(b.node), 'fr', { sensitivity: 'base' }));
    return out;
}

/** Ensemble des voisins directs (pour la mise en avant au survol / à la sélection). */
export function neighborIds(view: GraphView, nodeId: string): Set<string> {
    const ids = new Set<string>();
    for (const edge of view.edges) {
        if (edge.source === nodeId) ids.add(edge.target);
        else if (edge.target === nodeId) ids.add(edge.source);
    }
    return ids;
}

/** Les `count` nœuds les plus connectés, les foyers d'abord : ceux dont le libellé reste visible à tout niveau de zoom. */
export function labelPriority(view: GraphView, count: number): Set<string> {
    const sorted = [...view.nodes].sort((a, b) => (Number(isFoyer(b)) - Number(isFoyer(a))) || ((view.degree.get(b.id) ?? 0) - (view.degree.get(a.id) ?? 0)));
    return new Set(sorted.slice(0, Math.max(0, count)).map(n => n.id));
}

/** Le nombre de foyers affichés (légende). */
export const foyerCount = (view: GraphView): number => view.nodes.filter(isFoyer).length;

/**
 * La distance en sauts de chaque nœud affiché aux foyers (19/10/2026) : 0 pour un foyer, 1 pour
 * ses voisins, etc. ; un nœud que rien ne relie aux foyers prend le saut le plus lointain + 1.
 * Vide quand la vue n'a aucun foyer : la mise en page reste alors celle des forces seules.
 */
export function hopsFromFoyers(view: GraphView): Map<string, number> {
    const hops = new Map<string, number>();
    const file: string[] = [];
    for (const n of view.nodes) {
        if (isFoyer(n)) { hops.set(n.id, 0); file.push(n.id); }
    }
    if (file.length === 0) return hops;
    const voisins = new Map<string, string[]>();
    for (const e of view.edges) {
        (voisins.get(e.source) ?? voisins.set(e.source, []).get(e.source)!).push(e.target);
        (voisins.get(e.target) ?? voisins.set(e.target, []).get(e.target)!).push(e.source);
    }
    let max = 0;
    for (let i = 0; i < file.length; i += 1) {
        const id = file[i];
        const h = hops.get(id)!;
        for (const v of voisins.get(id) ?? []) {
            if (hops.has(v)) continue;
            hops.set(v, h + 1);
            max = Math.max(max, h + 1);
            file.push(v);
        }
    }
    for (const n of view.nodes) if (!hops.has(n.id)) hops.set(n.id, max + 1);
    return hops;
}

/** Les groupes restreints aux nœuds affichés ; un groupe sans membre visible disparaît. */
export function groupesAffiches(groupes: ReadonlyArray<ReadonlyArray<string>>, view: GraphView): string[][] {
    const visibles = new Set(view.nodes.map(n => n.id));
    return groupes.map(groupe => groupe.filter(id => visibles.has(id))).filter(groupe => groupe.length > 0);
}

/** Le groupe auquel appartient un nœud, ou `null`. */
export function groupeDe(groupes: ReadonlyArray<ReadonlyArray<string>>, nodeId: string): string[] | null {
    const groupe = groupes.find(g => g.includes(nodeId));
    return groupe ? [...groupe] : null;
}
